import { emptyTimeline, generateDraft, markLineStart, parseTimeline, round3, serializeLrc, serializeTimeline } from '../timeline';
import type { Timeline, Track } from '../types';
import { download, fmtTime, h, ICON, pickFiles, toast } from './dom';

export interface TimelineHost {
  track(): Track | undefined;
  timeline(): Timeline | null;
  edited(): boolean;
  /** 提交修改（会保存到本地） */
  commit(tl: Timeline): void;
  revert(): Promise<void>;
  /** 音频时间 → 时间轴时间（扣除全局偏移与时间轴 offset） */
  timelineTime(): number;
  seekTimeline(t: number): void;
  togglePlay(): void;
}

const STATUS_LABEL: Record<string, string> = { aligned: '已对齐', draft: '草稿', test: '测试用' };

/**
 * 时间轴编辑器：逐句编辑、打点对齐、模板草稿、导入导出（JSON / LRC）。
 */
export class TimelineEditor {
  readonly el: HTMLElement;
  private tbody: HTMLElement;
  private titleEl: HTMLElement;
  private statusSel: HTMLSelectElement;
  private offsetInput: HTMLInputElement;
  private markBtn: HTMLButtonElement;
  private markIndex = 0;
  private activeRow = -1;
  private rows: HTMLElement[] = [];
  private clock: HTMLElement;
  private playBtn: HTMLButtonElement;

  constructor(private readonly host: TimelineHost) {
    this.titleEl = h('div', { class: 'tl-title' });
    this.statusSel = h(
      'select',
      { 'aria-label': '时间轴状态' },
      ...Object.entries(STATUS_LABEL).map(([v, l]) => h('option', { value: v }, l)),
    );
    this.statusSel.addEventListener('change', () => this.mutate((tl) => (tl.status = this.statusSel.value as Timeline['status'])));
    this.offsetInput = h('input', { type: 'number', step: 0.01, class: 'num', 'aria-label': '时间轴偏移（秒）' });
    this.offsetInput.addEventListener('change', () => this.mutate((tl) => (tl.offset = round3(Number(this.offsetInput.value) || 0))));
    this.markBtn = h('button', { class: 'btn primary big', onclick: () => this.mark() }, '打点');
    this.tbody = h('tbody');
    this.clock = h('span', { class: 'tl-clock mono' }, '0.00');
    this.playBtn = h('button', { class: 'btn', onclick: () => this.host.togglePlay() }, '播放');

    const draftText = h('input', { type: 'text', class: 'text', value: '南无阿弥陀佛', 'aria-label': '草稿文字' });
    const draftStart = h('input', { type: 'number', class: 'num', value: '0', step: 0.1, 'aria-label': '起始秒' });
    const draftInterval = h('input', { type: 'number', class: 'num', value: '4', step: 0.1, 'aria-label': '间隔秒' });
    const draftCount = h('input', { type: 'number', class: 'num', value: '20', step: 1, 'aria-label': '句数' });

    this.el = h(
      'div',
      { id: 'tl-editor', class: 'sheet', role: 'dialog', 'aria-label': '歌词时间轴编辑器' },
      h(
        'div',
        { class: 'sheet-head' },
        this.titleEl,
        h('button', { class: 'icon-btn', title: '关闭', html: ICON.close, onclick: () => this.toggle(false) }),
      ),
      h(
        'div',
        { class: 'tl-toolbar' },
        h('label', { class: 'inline' }, '状态 ', this.statusSel),
        h('label', { class: 'inline' }, '本曲偏移 ', this.offsetInput, ' 秒'),
        h('button', { class: 'btn', onclick: () => this.importFile() }, '导入 JSON/LRC'),
        h('button', { class: 'btn', onclick: () => this.exportJson() }, '导出 JSON'),
        h('button', { class: 'btn', onclick: () => this.exportLrc() }, '导出 LRC'),
        h('button', { class: 'btn', onclick: () => void this.host.revert().then(() => this.refresh()) }, '恢复原始'),
      ),
      h(
        'details',
        { class: 'tl-draft' },
        h('summary', {}, '按模板生成草稿（需再按真实音频校对）'),
        h(
          'div',
          { class: 'tl-toolbar' },
          h('label', { class: 'inline' }, '文字 ', draftText),
          h('label', { class: 'inline' }, '起始 ', draftStart),
          h('label', { class: 'inline' }, '间隔 ', draftInterval),
          h('label', { class: 'inline' }, '句数 ', draftCount),
          h(
            'button',
            {
              class: 'btn',
              onclick: () => {
                const interval = Number(draftInterval.value) || 4;
                const tl = generateDraft({
                  text: draftText.value.trim() || '南无阿弥陀佛',
                  start: Number(draftStart.value) || 0,
                  interval,
                  duration: interval * 0.9,
                  count: Math.max(1, Math.min(2000, Number(draftCount.value) || 1)),
                  trackId: this.host.track()?.id,
                });
                this.host.commit(tl);
                this.refresh();
              },
            },
            '生成（替换当前）',
          ),
        ),
      ),
      h(
        'div',
        { class: 'tl-toolbar tl-transport' },
        this.playBtn,
        h('button', { class: 'btn', title: '后退 2 秒', onclick: () => this.host.seekTimeline(this.host.timelineTime() - 2) }, '−2 秒'),
        h('button', { class: 'btn', title: '前进 2 秒', onclick: () => this.host.seekTimeline(this.host.timelineTime() + 2) }, '+2 秒'),
        h('span', { class: 'inline' }, '时间轴时间 ', this.clock, ' 秒'),
      ),
      h(
        'div',
        { class: 'tl-mark' },
        this.markBtn,
        h('div', { class: 'hint' }, '打点对齐：播放时在每句开口处按「打点」或键盘 M，依次把当前时间写入下一句的开始，并自动收尾上一句。点「#」列可从某句重新开始打点。'),
      ),
      h(
        'div',
        { class: 'tl-table-wrap' },
        h(
          'table',
          { class: 'tl-table' },
          h('thead', {}, h('tr', {}, h('th', {}, '#'), h('th', {}, '开始'), h('th', {}, '结束'), h('th', {}, '文字'), h('th', {}, ''))),
          this.tbody,
        ),
      ),
      h(
        'div',
        { class: 'btn-row' },
        h('button', { class: 'btn', onclick: () => this.addAtCurrent() }, '在当前时间插入一句'),
      ),
    );
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  get open(): boolean {
    return this.el.classList.contains('open');
  }

  toggle(force?: boolean): void {
    const open = force ?? !this.open;
    this.el.classList.toggle('open', open);
    document.body.classList.toggle('editor-open', open);
    if (open) this.refresh();
  }

  private current(): Timeline {
    return structuredClone(this.host.timeline() ?? emptyTimeline(this.host.track()?.id));
  }

  private mutate(fn: (tl: Timeline) => void, rebuild = false): void {
    const tl = this.current();
    fn(tl);
    tl.lines.sort((a, b) => a.start - b.start);
    this.host.commit(tl);
    if (rebuild) this.refresh();
    else this.updateTitle();
  }

  private updateTitle(): void {
    const tr = this.host.track();
    const tl = this.host.timeline();
    const status = tl?.status ? STATUS_LABEL[tl.status] : '无';
    this.titleEl.textContent = `时间轴 · ${tr?.title ?? '未选择曲目'} · ${tl?.lines.length ?? 0} 句 · ${status}${this.host.edited() ? ' · 本地已编辑' : ''}`;
  }

  refresh(): void {
    if (!this.open) return;
    const tl = this.current();
    this.updateTitle();
    this.statusSel.value = tl.status ?? 'draft';
    this.offsetInput.value = String(tl.offset ?? 0);
    this.markIndex = Math.min(this.markIndex, tl.lines.length);
    this.updateMarkBtn(tl);
    this.tbody.innerHTML = '';
    this.rows = tl.lines.map((line, i) => {
      const start = h('input', { type: 'number', step: 0.01, class: 'num', value: String(line.start), 'aria-label': `第 ${i + 1} 句开始` });
      const end = h('input', { type: 'number', step: 0.01, class: 'num', value: line.end !== undefined ? String(line.end) : '', placeholder: '自动', 'aria-label': `第 ${i + 1} 句结束` });
      const text = h('input', { type: 'text', class: 'text', value: line.text, 'aria-label': `第 ${i + 1} 句文字` });
      start.addEventListener('change', () => this.mutate((t) => (t.lines[i].start = round3(Number(start.value) || 0)), true));
      end.addEventListener('change', () =>
        this.mutate((t) => {
          if (end.value === '') delete t.lines[i].end;
          else t.lines[i].end = round3(Number(end.value));
        }),
      );
      text.addEventListener('change', () => this.mutate((t) => (t.lines[i].text = text.value)));
      const row = h(
        'tr',
        {},
        h('td', {}, h('button', { class: 'link', title: '从这一句开始打点', onclick: () => { this.markIndex = i; this.updateMarkBtn(this.current()); } }, String(i + 1))),
        h('td', {}, start),
        h('td', {}, end),
        h('td', {}, text),
        h(
          'td',
          { class: 'row-actions' },
          h('button', { class: 'mini', title: '跳到此句', onclick: () => this.host.seekTimeline(line.start) }, '跳转'),
          h('button', { class: 'mini', title: '开始设为当前时间', onclick: () => this.mutate((t) => (t.lines = markLineStart(t.lines, i, this.host.timelineTime())), true) }, '设起'),
          h('button', { class: 'mini', title: '结束设为当前时间', onclick: () => this.mutate((t) => (t.lines[i].end = round3(this.host.timelineTime())), true) }, '设止'),
          h('button', { class: 'mini danger', title: '删除此句', onclick: () => this.mutate((t) => t.lines.splice(i, 1), true) }, '删'),
        ),
      );
      this.tbody.appendChild(row);
      return row;
    });
    this.activeRow = -1;
  }

  private updateMarkBtn(tl: Timeline): void {
    const n = tl.lines.length;
    this.markBtn.textContent = n === 0 ? '打点（先生成草稿或插入句子）' : this.markIndex < n ? `打点 → 第 ${this.markIndex + 1} / ${n} 句「${tl.lines[this.markIndex].text}」` : '已打到最后一句';
    this.markBtn.disabled = n === 0 || this.markIndex >= n;
  }

  /** 打点：键盘 M 也会调用 */
  mark(): void {
    const tl = this.current();
    if (this.markIndex >= tl.lines.length) return;
    const t = this.host.timelineTime();
    tl.lines = markLineStart(tl.lines, this.markIndex, t);
    this.host.commit(tl);
    this.markIndex++;
    this.refresh();
    this.rows[this.markIndex - 1]?.scrollIntoView({ block: 'center' });
  }

  private addAtCurrent(): void {
    const t = round3(this.host.timelineTime());
    const tl = this.current();
    const prev = [...tl.lines].reverse().find((l) => l.start <= t);
    this.mutate((x) => x.lines.push({ start: t, text: prev?.text ?? '南无阿弥陀佛' }), true);
    toast(`已在 ${fmtTime(t)} 插入一句`);
  }

  /** 每帧调用：高亮当前句、刷新时钟 */
  setActive(index: number, playing = false): void {
    if (!this.open) return;
    this.clock.textContent = this.host.timelineTime().toFixed(2);
    const label = playing ? '暂停' : '播放';
    if (this.playBtn.textContent !== label) this.playBtn.textContent = label;
    if (index === this.activeRow) return;
    this.rows[this.activeRow]?.classList.remove('active');
    this.activeRow = index;
    this.rows[index]?.classList.add('active');
  }

  private async importFile(): Promise<void> {
    const [f] = await pickFiles('.json,.lrc,application/json,text/plain');
    if (!f) return;
    try {
      const tl = parseTimeline(await f.text(), this.host.track()?.id);
      const tid = this.host.track()?.id;
      if (tl.trackId && tid && tl.trackId !== tid) toast(`提示：文件中的 trackId（${tl.trackId}）与当前曲目不同，已按当前曲目导入`, 4000);
      tl.trackId = tid;
      this.host.commit(tl);
      this.markIndex = 0;
      this.refresh();
      toast(`已导入 ${tl.lines.length} 句`);
    } catch (e) {
      toast('导入失败：' + (e as Error).message, 4000);
    }
  }

  private exportJson(): void {
    const tl = this.host.timeline();
    if (!tl) return toast('当前曲目没有时间轴');
    download(`${this.host.track()?.id ?? 'timeline'}.timeline.json`, serializeTimeline(tl));
  }

  private exportLrc(): void {
    const tl = this.host.timeline();
    if (!tl) return toast('当前曲目没有时间轴');
    download(`${this.host.track()?.id ?? 'timeline'}.lrc`, serializeLrc(tl), 'text/plain');
  }
}
